import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Coins, Download, Loader2, Pencil, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/store/useApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/api";
import { hasPermission, type CatalogUnit } from "@/lib/types";
import { fetchCatalogUnits } from "@/lib/catalogApi";
import {
  INCOMPLETE_REASON_LABELS,
  PACKAGING_REASON_LABELS,
  exportProductCostsXlsx,
  fetchProductCosts,
  type ProductCostListItem,
} from "@/lib/adminProductCostsApi";
import { ProductCostPanel } from "@/components/admin/ProductCostPanel";
import { CostImportDialog } from "@/components/admin/CostImportDialog";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const formatBRL = (value: number | null) => (value == null ? "—" : brl.format(value));
const formatPercent = (value: number | null) =>
  value == null ? "—" : `${Number.isInteger(value) ? value : value.toFixed(2).replace(/\.?0+$/, "")}%`;

// Razões humanizadas reutilizam os mapas do api. O mesmo código (UNIDADES_INCOMPATIVEIS) tem
// sentidos distintos: no custo BASE = unidade operacional vs. matéria-prima; na APRESENTAÇÃO =
// embalagem vs. unidade de referência. Por isso a prioridade de mapa depende do contexto.
const baseReasonLabel = (code: string) =>
  INCOMPLETE_REASON_LABELS[code] ?? PACKAGING_REASON_LABELS[code] ?? code;
const presentationReasonLabel = (code: string) =>
  PACKAGING_REASON_LABELS[code] ?? INCOMPLETE_REASON_LABELS[code] ?? code;

// chevron + 8 colunas: Produto | Unidade/apresentação | Matéria-prima | Operacional |
// Merma (R$ e %) | Embalagem | Custo base | Total com embalagem
const GRID = "md:grid-cols-[auto_1.6fr_1.3fr_1fr_1fr_1.1fr_0.9fr_1fr_1.1fr]";

export default function AdminProductCosts() {
  const account = useApp((state) => state.account);
  const canEdit = hasPermission(account, "CATALOGO.CUSTO.EDITAR");

  const [items, setItems] = useState<ProductCostListItem[]>([]);
  const [units, setUnits] = useState<CatalogUnit[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [editing, setEditing] = useState<ProductCostListItem | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Motivos de incompletude revelados por clique/foco (além do tooltip), por célula.
  const [openReasons, setOpenReasons] = useState<Set<string>>(new Set());
  const toggleReasons = (key: string) =>
    setOpenReasons((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const load = useCallback(async () => {
    setLoadState("loading");
    setLoadError(null);
    try {
      const [costs, catalogUnits] = await Promise.all([fetchProductCosts(), fetchCatalogUnits()]);
      setItems(costs);
      setUnits(catalogUnits);
      setLoadState("ready");
    } catch (error) {
      setLoadState("error");
      setLoadError(error instanceof Error ? error.message : "Não foi possível carregar os custos");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      // "Incompletos" = custo base incompleto (NUNCA embalagem/total ausente).
      if (onlyIncomplete && item.complete) return false;
      if (!q) return true;
      return item.name.toLowerCase().includes(q) || (item.code != null && String(item.code).includes(q));
    });
  }, [items, query, onlyIncomplete]);

  const incompleteCount = useMemo(() => items.filter((i) => !i.complete).length, [items]);

  const toggleRow = (productId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });

  const expandAll = () => setExpanded(new Set(filtered.map((i) => i.productId)));
  const collapseAll = () => setExpanded(new Set());

  // Exporta a consulta atual (com os mesmos filtros) — o backend cobre todas as linhas do resultado.
  const onExport = async () => {
    setExporting(true);
    try {
      await exportProductCostsXlsx(query, onlyIncomplete);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível exportar os custos"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container flex flex-col gap-4 py-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Custos dos produtos</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Base de custos (matéria-prima, operacional e merma) e custo com embalagem por apresentação.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm"
              className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={onExport} disabled={exporting || loadState !== "ready"}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Exportar XLSX
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm"
                className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" /> Importar XLSX
              </Button>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Stat label="Produtos" value={items.length} />
              <Stat label="Com custo" value={items.filter((i) => i.hasCost).length} />
              <Stat label="Incompletos" value={incompleteCount} />
            </div>
          </div>
        </div>
      </section>

      <CostImportDialog
        open={importOpen}
        basePath="/api/v1/admin/product-costs/import"
        templateFilename="modelo_custos_materia_prima.xlsx"
        title="Importar custos de matéria-prima (XLSX)"
        canEdit={canEdit}
        onClose={() => setImportOpen(false)}
        onApplied={() => void load()}
      />

      <div className="container space-y-5 py-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          {/* h-14 fixa a altura do wrapper = altura do Input; sem isto o stretch da linha (botão + legenda à
              direita, mais altos) esticava o wrapper e a lupa caía abaixo do campo. */}
          <div className="relative h-14 flex-1">
            {/* Centralização vertical da lupa: ocupa toda a altura do campo e alinha ao centro. */}
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-muted-foreground">
              <Search className="h-5 w-5" />
            </span>
            <Input
              placeholder="Buscar por código ou nome..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="h-14 bg-surface pl-12 text-base"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Button
              variant={onlyIncomplete ? "hero" : "outline"}
              onClick={() => setOnlyIncomplete((v) => !v)}
              className="h-14 shrink-0 px-5"
            >
              {onlyIncomplete ? "Mostrando incompletos" : "Só custos incompletos"}
            </Button>
            <p className="max-w-[16rem] text-[11px] leading-tight text-muted-foreground">
              incompletos = custo base incompleto (matéria-prima/operacional/origem); não considera
              embalagem ausente
            </p>
          </div>
        </div>

        {loadState === "ready" && filtered.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={expandAll}>
              Expandir todos
            </Button>
            <span className="text-muted-foreground">/</span>
            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={collapseAll}>
              Recolher todos
            </Button>
          </div>
        )}

        {loadState === "loading" && (
          <div className="surface-card p-12 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" /> Carregando custos...
          </div>
        )}
        {loadState === "error" && (
          <div className="surface-card p-12 text-center text-sm text-destructive">
            {loadError ?? "Não foi possível carregar os custos"}
          </div>
        )}

        {loadState === "ready" && (
          <div className="surface-card overflow-hidden">
            <div
              className={cn(
                "hidden gap-4 border-b border-border bg-secondary/40 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground md:grid",
                GRID,
              )}
            >
              <div aria-hidden />
              <div>Produto</div>
              <div>Unidade/apresentação</div>
              <div>Matéria-prima</div>
              <div>Operacional</div>
              <div>Merma (R$ e %)</div>
              <div>Embalagem</div>
              <div>Custo base</div>
              <div>Total com embalagem</div>
            </div>
            <ul className="divide-y divide-border">
              {filtered.map((item) => {
                const isOpen = expanded.has(item.productId);
                const opMode = item.operationalMode;
                // FIXED → "(fixo)"; PERCENT → efeito em R$ + percentual real "(y% s/ matéria-prima)".
                // Fallback sem número só se operationalPercent vier null (não deve ocorrer em PERCENT).
                const opText =
                  opMode == null
                    ? "—"
                    : opMode === "FIXED"
                      ? `${formatBRL(item.operationalCost)} (fixo)`
                      : item.operationalPercent == null
                        ? `${formatBRL(item.operationalCost)} (% s/ matéria-prima)`
                        : `${formatBRL(item.operationalCost)} (${formatPercent(item.operationalPercent)} s/ matéria-prima)`;
                return (
                  <li key={item.productId} className={cn(!item.active && "opacity-60")}>
                    {/* MAIN ROW (por produto) */}
                    <div
                      className={cn(
                        "grid items-center gap-4 px-5 py-3 transition-colors hover:bg-secondary/30",
                        GRID,
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleRow(item.productId)}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? "Recolher apresentações" : "Expandir apresentações"}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary"
                        disabled={item.presentations.length === 0}
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>

                      <button
                        type="button"
                        className="min-w-0 text-left"
                        onClick={() => setEditing(item)}
                        title={canEdit ? "Editar custos" : "Ver detalhes"}
                      >
                        <div className="truncate font-display font-semibold">{item.name}</div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {item.code != null && <span className="tabular-nums">Cód. {item.code}</span>}
                          {!item.complete && (
                            <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warning">
                              Incompleto
                            </span>
                          )}
                        </div>
                        {item.inheritsRawCost && (
                          <div className="text-[11px] not-italic text-muted-foreground">
                            herdado de {item.originProductName ?? "—"}
                          </div>
                        )}
                      </button>

                      {/* Unidade de referência do custo base (NÃO a apresentação). */}
                      <div className="text-xs text-muted-foreground">
                        {item.rawCostUnitName ? (
                          <span>ref.: {item.rawCostUnitName}</span>
                        ) : (
                          <span>ref.: —</span>
                        )}
                      </div>

                      <div className="text-sm tabular-nums">
                        {formatBRL(item.rawCost)}
                        {item.rawCostUnitName && (
                          <span className="text-xs text-muted-foreground"> / {item.rawCostUnitName}</span>
                        )}
                      </div>

                      <div className="text-sm tabular-nums">{opText}</div>

                      <div className="text-sm tabular-nums">
                        {formatBRL(item.mermaSurcharge)}{" "}
                        <span className="text-xs text-muted-foreground">
                          ({formatPercent(item.mermaUsedPercent)})
                        </span>
                        {item.mermaDefault && (
                          <span
                            className="ml-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
                            title="produto sem perda cadastrada"
                          >
                            padrão
                          </span>
                        )}
                      </div>

                      {/* Embalagem é por apresentação — vazio na linha principal. */}
                      <div aria-hidden />

                      <div className="text-sm tabular-nums">
                        {item.baseCost == null ? (
                          <IncompleteReasons
                            reasonKey={`base:${item.productId}`}
                            labels={item.baseReasons.map(baseReasonLabel)}
                            fallback="Custo base incompleto"
                            open={openReasons.has(`base:${item.productId}`)}
                            onToggle={toggleReasons}
                          />
                        ) : (
                          formatBRL(item.baseCost)
                        )}
                      </div>

                      {/* Total com embalagem é por apresentação — vazio na linha principal. */}
                      <div className="text-sm text-muted-foreground">—</div>
                    </div>

                    {/* SUB-ROWS (uma por apresentação) */}
                    {isOpen &&
                      item.presentations.map((p) => (
                        <div
                          key={p.unitId}
                          className={cn(
                            "grid items-center gap-4 border-t border-dashed border-border bg-secondary/10 px-5 py-2",
                            GRID,
                          )}
                        >
                          <div aria-hidden />
                          <div className="text-xs text-muted-foreground">↳ apresentação</div>
                          <div className="text-sm">{p.unitName}</div>
                          {/* Matéria-prima / Operacional / Merma são por unidade de referência,
                              não por apresentação — em branco na sub-linha. */}
                          <div aria-hidden />
                          <div aria-hidden />
                          <div aria-hidden />
                          {/* Embalagem (por apresentação) */}
                          <div className="text-sm tabular-nums">
                            <span className="text-xs text-muted-foreground md:hidden">Embalagem: </span>
                            {formatBRL(p.packagingCost)}
                          </div>
                          {/* Custo base é por unidade de referência — em branco na sub-linha. */}
                          <div aria-hidden />
                          {/* Total com embalagem (por apresentação) */}
                          <div className="text-sm tabular-nums">
                            {p.totalComplete ? (
                              formatBRL(p.totalWithPackaging)
                            ) : (
                              <IncompleteReasons
                                reasonKey={`total:${item.productId}:${p.unitId}`}
                                labels={p.totalReasons.map(presentationReasonLabel)}
                                fallback="Total com embalagem indisponível"
                                open={openReasons.has(`total:${item.productId}:${p.unitId}`)}
                                onToggle={toggleReasons}
                              />
                            )}
                          </div>
                        </div>
                      ))}

                    {isOpen && item.presentations.length === 0 && (
                      <div className="border-t border-dashed border-border bg-secondary/10 px-5 py-2 text-xs text-muted-foreground">
                        Nenhuma apresentação com embalagem cadastrada.
                      </div>
                    )}
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="px-5 py-12 text-center text-sm text-muted-foreground">Nenhum produto encontrado.</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <Dialog open={editing !== null} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Custos — {editing?.name}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <ProductCostPanel
              productId={editing.productId}
              canEdit={canEdit}
              units={units}
              onSaved={() => void load()}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Indicação visível de incompletude + motivos legíveis por clique/foco (não só tooltip/tracinho).
function IncompleteReasons({
  reasonKey,
  labels,
  fallback,
  open,
  onToggle,
}: {
  reasonKey: string;
  labels: string[];
  fallback: string;
  open: boolean;
  onToggle: (key: string) => void;
}) {
  const reasons = labels.length > 0 ? labels : [fallback];
  const tooltip = reasons.join("; ");
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">—</span>
        <button
          type="button"
          onClick={() => onToggle(reasonKey)}
          aria-expanded={open}
          aria-label={`Incompleto: ${tooltip}`}
          title={tooltip}
          className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warning hover:bg-warning/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-warning"
        >
          Incompleto
        </button>
      </div>
      {open && (
        <ul className="list-disc space-y-0.5 pl-4 text-left text-[11px] font-normal leading-tight text-muted-foreground">
          {reasons.map((reason, index) => (
            <li key={index}>{reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-primary-foreground/20 bg-primary-foreground/10 px-3 py-2 backdrop-blur-sm">
      <div className="font-display text-lg font-bold leading-none tabular-nums">{value}</div>
      <div className="mt-1 opacity-80">{label}</div>
    </div>
  );
}
